import { useCallback } from "react";
import { useStoreState } from "pullstate";
import ACRStore from "../../../utils/ACRStore";
import Toast from "../../Toast";
import useSubmitACR from "../services/useSubmitACR";
import useSaveSubmissionForm from "../services/useSaveSubmissionForm";
import { useTranslation } from "react-i18next";
import { ACR_QUERY_KEYS } from "../../../constants/acr";
import { handlePartialValidationError, updateStoreAfterSaveOrSubmit } from "../../../utils/acrStoreUtils";
import { isEmptyValue, isShouldBeBlankError } from "../../../utils/formValueUtils";
import { clearChangedFieldsAndErrors, storeFieldErrors as storeFieldErrorsUtil } from "../../../utils/submissionErrorUtils";
var parseFieldErrors = function parseFieldErrors(error) {
  var _error$response;
  var fieldErrors = error === null || error === void 0 || (_error$response = error.response) === null || _error$response === void 0 || (_error$response = _error$response.data) === null || _error$response === void 0 ? void 0 : _error$response.field_errors;
  if (!fieldErrors || !Array.isArray(fieldErrors) || fieldErrors.length === 0) {
    return null;
  }
  var errorMap = {};
  fieldErrors.forEach(function (fe) {
    if (fe !== null && fe !== void 0 && fe.field && fe !== null && fe !== void 0 && fe.error) {
      errorMap[fe.field] = fe.error;
    }
  });
  return errorMap;
};
var parseFullFieldErrors = function parseFullFieldErrors(error) {
  var _error$response2;
  var fieldErrors = error === null || error === void 0 || (_error$response2 = error.response) === null || _error$response2 === void 0 || (_error$response2 = _error$response2.data) === null || _error$response2 === void 0 ? void 0 : _error$response2.field_errors;
  if (!fieldErrors || !Array.isArray(fieldErrors) || fieldErrors.length === 0) {
    return null;
  }
  return fieldErrors;
};

// Build changed field data from store
var buildChangedFieldData = function buildChangedFieldData(submissionDetail, submissionID, changedFieldsSet) {
  var _storeState$submissio, _storeState$submissio2, _storeState$fullField;
  var storeState = ACRStore.getRawState();
  var storeChangedFields = (_storeState$submissio = storeState.submissionChangedFields) === null || _storeState$submissio === void 0 || (_storeState$submissio = _storeState$submissio[submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.patient_id]) === null || _storeState$submissio === void 0 ? void 0 : _storeState$submissio[submissionID];
  var storeFormData = (_storeState$submissio2 = storeState.submissionFormData) === null || _storeState$submissio2 === void 0 || (_storeState$submissio2 = _storeState$submissio2[submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.patient_id]) === null || _storeState$submissio2 === void 0 ? void 0 : _storeState$submissio2[submissionID];
  var fullFieldErrors = ((_storeState$fullField = storeState.fullFieldErrors) === null || _storeState$fullField === void 0 ? void 0 : _storeState$fullField[submissionID]) || [];
  var changedFieldData = {};
  var fieldsToUse = storeChangedFields || changedFieldsSet;
  var dataToUse = storeFormData;

  // Include changed fields (exclude patient_identifier)
  if (fieldsToUse && dataToUse) {
    fieldsToUse.forEach(function (fieldName) {
      if (fieldName !== "patient_identifier") {
        changedFieldData[fieldName] = dataToUse === null || dataToUse === void 0 ? void 0 : dataToUse[fieldName];
      }
    });
  }

  // Include ALL missing fields (empty or filled, including static fields)
  var missingFields = (submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.missing_fields) || [];
  if (missingFields.length > 0 && dataToUse) {
    missingFields.forEach(function (fieldName) {
      // Skip patient_identifier
      if (fieldName === "patient_identifier") return;

      // Only add if not already in changedFieldData
      if (!changedFieldData.hasOwnProperty(fieldName)) {
        var fieldValue = dataToUse === null || dataToUse === void 0 ? void 0 : dataToUse[fieldName];
        // Send the actual value (null if empty, or the value if filled)
        changedFieldData[fieldName] = fieldValue !== undefined ? fieldValue : null;
      }
    });
  }

  // Include child fields with should_be_blank errors that are empty
  if (fullFieldErrors.length > 0 && dataToUse) {
    fullFieldErrors.forEach(function (error) {
      if (isShouldBeBlankError(error)) {
        var childFieldName = error.field;

        // Skip patient_identifier
        if (childFieldName === "patient_identifier") return;
        var fieldValue = dataToUse === null || dataToUse === void 0 ? void 0 : dataToUse[childFieldName];

        // Only add if not already in changedFieldData and is empty
        if (isEmptyValue(fieldValue) && !changedFieldData.hasOwnProperty(childFieldName)) {
          changedFieldData[childFieldName] = null;
        }
      }
    });
  }
  return changedFieldData;
};

// Store field-level errors in ACRStore
var storeFieldErrors = function storeFieldErrors(error, submissionID) {
  var fieldErrorMap = parseFieldErrors(error);
  var fullFieldErrors = parseFullFieldErrors(error);
  storeFieldErrorsUtil(fieldErrorMap, fullFieldErrors, submissionID);
};

/**
 * Custom hook to handle submission actions (save and submit to ACR)
 */
var useSubmissionActions = function useSubmissionActions(submissionID, submissionDetail, workspaceId, _onSuccess, queryClient, patientId, onErrorSwitchToMissingTab) {
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useSubmitACR = useSubmitACR(),
    submitToACR = _useSubmitACR.mutate,
    isSubmitting = _useSubmitACR.isLoading;
  var _useSaveSubmissionFor = useSaveSubmissionForm(),
    saveFields = _useSaveSubmissionFor.mutate,
    isSaving = _useSaveSubmissionFor.isLoading;
  var changedFieldsSet = useStoreState(ACRStore, function (s) {
    var _s$submissionChangedF;
    return (_s$submissionChangedF = s.submissionChangedFields) === null || _s$submissionChangedF === void 0 || (_s$submissionChangedF = _s$submissionChangedF[(submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.patient_id) || ""]) === null || _s$submissionChangedF === void 0 ? void 0 : _s$submissionChangedF[submissionID || 0];
  }, [submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.patient_id, submissionID]);

  // Handle submit to ACR
  var handleSubmit = useCallback(function () {
    if (!submissionID || !submissionDetail) {
      console.error(t("label.acr.no_submission_id_or_detail"));
      return;
    }
    var changedFieldData = buildChangedFieldData(submissionDetail, submissionID, changedFieldsSet);
    submitToACR({
      submissionId: submissionID,
      workspaceId: workspaceId,
      field_data: changedFieldData
    }, {
      onSuccess: function onSuccess() {
        // Clear changed fields and field errors after successful submit
        clearChangedFieldsAndErrors(submissionDetail.patient_id, submissionID);

        // Invalidate submissions query to refresh the list
        if (queryClient && patientId) {
          var _queryClient$invalida;
          (_queryClient$invalida = queryClient.invalidateQueries) === null || _queryClient$invalida === void 0 || _queryClient$invalida.call(queryClient, ["acrSubmissions", workspaceId, patientId]);
        }
        Toast.success(t("toast.successfully_submitted_exam_id_to_acr", {
          submissionID: submissionID
        }));
        _onSuccess === null || _onSuccess === void 0 || _onSuccess();
      },
      onError: function onError(error) {
        var _error$response3, _error$response4, _Object$keys;
        console.error(t("label.acr.submit_failed"), error);
        var status = (error === null || error === void 0 || (_error$response3 = error.response) === null || _error$response3 === void 0 ? void 0 : _error$response3.status) || (error === null || error === void 0 ? void 0 : error.status);
        var data = error === null || error === void 0 || (_error$response4 = error.response) === null || _error$response4 === void 0 ? void 0 : _error$response4.data;

        // Handle 409 Conflict - submission already submitted
        if (status === 409) {
          Toast.error(t("toast.submission_already_in_progress_please_try_again_later"));
          return;
        }

        // Handle 403 Forbidden - no workspace access
        if (status === 403) {
          Toast.error(t("toast.no_permission_to_access_this_workspace"));
          return;
        }

        // IMPORTANT: Even though submission failed, backend saved the field_data and returns updated submission
        // Update store with the response data (status changed to "error", field_data updated)
        if (data && (data === null || data === void 0 ? void 0 : data.id) === submissionID) {
          // Update query cache with the new submission detail
          queryClient === null || queryClient === void 0 || queryClient.setQueryData([ACR_QUERY_KEYS.SUBMISSION_DETAIL, submissionID], data);

          // Update ACRStore with the response - this updates submissionDetail, formData, and originalFieldData
          updateStoreAfterSaveOrSubmit(data);

          // Clear changed fields since data was saved (new field_data becomes baseline)
          clearChangedFieldsAndErrors(submissionDetail.patient_id, submissionID);
        }

        // Store field-level errors
        storeFieldErrors(error, submissionID);

        // Handle partial validation errors - update stores to move completed fields to Completed tab
        handlePartialValidationError(error, submissionID, queryClient, ACR_QUERY_KEYS);

        // Store ACR error response for display in the form
        if (submissionID && data) {
          ACRStore.update(function (store) {
            if (!store.submitErrors) {
              store.submitErrors = {};
            }
            store.submitErrors[submissionID] = {
              user_message: (data === null || data === void 0 ? void 0 : data.user_message) || (data === null || data === void 0 ? void 0 : data.detail) || (data === null || data === void 0 ? void 0 : data.message),
              technical_details: data === null || data === void 0 ? void 0 : data.technical_details,
              acr_response: data === null || data === void 0 ? void 0 : data.acr_response
            };
          });
        }
        var fieldErrorMap = parseFieldErrors(error);

        // Switch to missing tab to show error fields
        if (fieldErrorMap && ((_Object$keys = Object.keys(fieldErrorMap)) === null || _Object$keys === void 0 ? void 0 : _Object$keys.length) > 0) {
          onErrorSwitchToMissingTab === null || onErrorSwitchToMissingTab === void 0 || onErrorSwitchToMissingTab();
        }

        // Handle 401 - ACR auth expired
        if (status === 401 && data !== null && data !== void 0 && data.requires_relogin) {
          Toast.error((data === null || data === void 0 ? void 0 : data.relogin_message) || t("toast.acr_session_expired_please_re_authenticate"));
          return;
        }

        // Handle categorized errors (400 and 502)
        if (data !== null && data !== void 0 && data.user_message) {
          Toast.error(data === null || data === void 0 ? void 0 : data.user_message);
          return;
        }

        // Fallback to generic error
        var errorMessage = (data === null || data === void 0 ? void 0 : data.detail) || (data === null || data === void 0 ? void 0 : data.message) || (error === null || error === void 0 ? void 0 : error.message) || t("toast.failed_to_submit_acr");
        Toast.error(errorMessage);
      }
    });
  }, [submissionID, submissionDetail, workspaceId, changedFieldsSet, submitToACR, _onSuccess, queryClient, patientId, onErrorSwitchToMissingTab]);

  // Handle save fields (without submitting to ACR)
  var handleSave = useCallback(function () {
    if (!submissionID || !submissionDetail) {
      console.error(t("label.acr.no_submission_id_or_detail"));
      return;
    }
    var changedFieldData = buildChangedFieldData(submissionDetail, submissionID, changedFieldsSet);
    saveFields({
      submissionId: submissionID,
      workspaceId: workspaceId,
      field_data: changedFieldData
    }, {
      onSuccess: function onSuccess(data) {
        var _data$validation_erro;
        clearChangedFieldsAndErrors(submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.patient_id, submissionID);

        // Show validation errors as warnings if present (non-blocking)
        if (data !== null && data !== void 0 && data.validation_errors && (data === null || data === void 0 || (_data$validation_erro = data.validation_errors) === null || _data$validation_erro === void 0 ? void 0 : _data$validation_erro.length) > 0) {
          var errorMessages = data === null || data === void 0 ? void 0 : data.validation_errors.map(function (err) {
            return "".concat(err === null || err === void 0 ? void 0 : err.field, ": ").concat(err === null || err === void 0 ? void 0 : err.error);
          }).join("; ");
          Toast.error("".concat(t("toast.saved_with_validation_warnings"), ": ").concat(errorMessages));
        } else {
          Toast.success(t("toast.successfully_saved_exam_id", {
            submissionID: submissionID
          }));
        }
      },
      onError: function onError(error) {
        var _error$response5, _error$response6;
        console.error("".concat(t("label.save_failed"), ":"), error);
        var status = error === null || error === void 0 || (_error$response5 = error.response) === null || _error$response5 === void 0 ? void 0 : _error$response5.status;
        var data = error === null || error === void 0 || (_error$response6 = error.response) === null || _error$response6 === void 0 ? void 0 : _error$response6.data;

        // Handle 403 Forbidden - no workspace access
        if (status === 403) {
          Toast.error(t("toast.no_permission_to_access_this_workspace"));
          return;
        }

        // Store field-level errors
        storeFieldErrors(error, submissionID);
        var fieldErrorMap = parseFieldErrors(error);
        if (fieldErrorMap && submissionID) {
          // Switch to missing tab to show error fields
          onErrorSwitchToMissingTab === null || onErrorSwitchToMissingTab === void 0 || onErrorSwitchToMissingTab();
        }

        // Show error message
        var errorMessage = (data === null || data === void 0 ? void 0 : data.detail) || (data === null || data === void 0 ? void 0 : data.message) || (error === null || error === void 0 ? void 0 : error.message) || t("toast.failed_to_save_fields");
        Toast.error(errorMessage);
      }
    });
  }, [submissionID, submissionDetail, workspaceId, changedFieldsSet, saveFields, onErrorSwitchToMissingTab]);
  return {
    handleSubmit: handleSubmit,
    handleSave: handleSave,
    isSubmitting: isSubmitting,
    isSaving: isSaving,
    changedFieldsSet: changedFieldsSet
  };
};
export default useSubmissionActions;